import React, { useState, useMemo, useRef, useCallback } from 'react';
import {
  LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
  BarChart, Bar, ComposedChart, Legend,
} from 'recharts';
import { Activity, Users, BarChart2, Download } from 'lucide-react';
import ChartCard from './ChartCard';
import { downloadChartPNG } from './downloadUtil';

const EMPTY_API_STATS = {
  totalApiHits: { label: 'Total API Hits', value: 0, change: 0 },
  avgHitsPerUser: { label: 'Avg Hits / User', value: 0, change: 0 },
  top10Contribution: { label: 'Top 10% Contribution', value: 0, change: 0, suffix: '%' },
};

// ─── Download Utilities ────────────────────────────────────────────────────
const downloadSVGasPNG = (containerEl, filename, isDark) => {
  if (!containerEl) return;
  const svg = containerEl.querySelector('svg');
  if (!svg) return;

  const clone = svg.cloneNode(true);
  const bbox = svg.getBoundingClientRect();
  clone.setAttribute('width', bbox.width);
  clone.setAttribute('height', bbox.height);
  const rect = document.createElementNS('http://www.w3.org/2000/svg', 'rect');
  rect.setAttribute('width', '100%');
  rect.setAttribute('height', '100%');
  rect.setAttribute('fill', isDark ? '#0f1729' : '#ffffff');
  clone.insertBefore(rect, clone.firstChild);

  const svgData = new XMLSerializer().serializeToString(clone);
  const blob = new Blob([svgData], { type: 'image/svg+xml;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  const img = new Image();
  img.onload = () => {
    const canvas = document.createElement('canvas');
    const scale = window.devicePixelRatio || 2;
    canvas.width = bbox.width * scale;
    canvas.height = bbox.height * scale;
    const ctx = canvas.getContext('2d');
    ctx.scale(scale, scale);
    ctx.fillStyle = isDark ? '#0f1729' : '#ffffff';
    ctx.fillRect(0, 0, bbox.width, bbox.height);
    ctx.drawImage(img, 0, 0, bbox.width, bbox.height);
    URL.revokeObjectURL(url);
    const link = document.createElement('a');
    link.download = `${filename}.png`;
    link.href = canvas.toDataURL('image/png');
    link.click();
  };
  img.src = url;
};

const downloadCSV = (rows, headers, filename) => {
  const csvContent = [
    headers.join(','),
    ...rows.map((r) => headers.map((h) => r[h.toLowerCase()] ?? r[h] ?? '').join(',')),
  ].join('\n');
  const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.download = `${filename}.csv`;
  link.href = url;
  link.click();
  URL.revokeObjectURL(url);
};

// ─── Download Button ───────────────────────────────────────────────────────
const DownloadMenu = ({ onPNG, onCSV, isDark }) => {
  const [open, setOpen] = useState(false);
  const ref = useRef();

  React.useEffect(() => {
    const handler = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const btnStyle = {
    display: 'flex', alignItems: 'center', gap: 5,
    padding: '5px 12px', borderRadius: 20, border: 'none', cursor: 'pointer',
    fontSize: 11, fontWeight: 600,
    background: isDark ? 'rgba(248,114,23,0.12)' : 'rgba(248,114,23,0.08)',
    color: '#F87217',
    transition: 'all 0.2s',
  };
  const hoverIn  = (e) => { e.currentTarget.style.background = isDark ? 'rgba(248,114,23,0.22)' : 'rgba(248,114,23,0.15)'; };
  const hoverOut = (e) => { e.currentTarget.style.background = isDark ? 'rgba(248,114,23,0.12)' : 'rgba(248,114,23,0.08)'; };

  // ── PNG-only mode: single button, no dropdown ──
  if (!onCSV) {
    return (
      <button
        onClick={onPNG}
        title="Export as PNG"
        data-download-btn
        style={btnStyle}
        onMouseEnter={hoverIn}
        onMouseLeave={hoverOut}
      >
        <Download size={13} />
        Export
      </button>
    );
  }

  // ── PNG + CSV mode: dropdown ──
  const menuBg    = isDark ? '#1a2540' : '#fff';
  const menuBorder = isDark ? '#1e2d52' : '#e2e8f0';
  const menuText  = isDark ? '#c9d1d9' : '#374151';
  const menuHover = isDark ? '#0f1729' : '#f8fafc';

  return (
    <div ref={ref} style={{ position: 'relative' }}>
      <button
        onClick={() => setOpen((p) => !p)}
        title="Download chart"
        style={btnStyle}
        onMouseEnter={hoverIn}
        onMouseLeave={hoverOut}
      >
        <Download size={13} />
        Export
      </button>

      {open && (
        <div style={{
          position: 'absolute', right: 0, top: 'calc(100% + 6px)', zIndex: 50,
          background: menuBg, border: `1px solid ${menuBorder}`, borderRadius: 10,
          boxShadow: '0 8px 24px rgba(0,0,0,0.18)', overflow: 'hidden', minWidth: 130,
        }}>
          {[
            { label: '📷  PNG Image', action: () => { onPNG(); setOpen(false); } },
            { label: '📄  CSV Data', action: () => { onCSV(); setOpen(false); } },
          ].map(({ label, action }) => (
            <button
              key={label}
              onClick={action}
              style={{
                display: 'block', width: '100%', textAlign: 'left',
                padding: '8px 14px', border: 'none', cursor: 'pointer',
                fontSize: 12, color: menuText, background: 'transparent',
                transition: 'background 0.15s',
              }}
              onMouseEnter={(e) => { e.currentTarget.style.background = menuHover; }}
              onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent'; }}
            >
              {label}
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

// ─── Shared Tooltip ────────────────────────────────────────────────────────
const CustomTooltip = ({ active, payload, label, isDark }) => {
  if (!active || !payload?.length) return null;
  return (
    <div style={{
      background: isDark ? '#0f1729' : '#fff',
      border: `1px solid ${isDark ? '#1e2d52' : '#e2e8f0'}`,
      borderRadius: 10, padding: '8px 12px', fontSize: 12,
      color: isDark ? '#c9d1d9' : '#374151',
      boxShadow: '0 4px 16px rgba(0,0,0,0.12)',
    }}>
      {label && <p style={{ fontWeight: 600, marginBottom: 4 }}>{label}</p>}
      {payload.map((p, i) => (
        <p key={i} style={{ color: p.color || p.stroke, margin: 0 }}>
          {p.name}: {typeof p.value === 'number' ? p.value.toLocaleString() : p.value}
          {p.name === 'Cumulative %' ? '%' : ''}
        </p>
      ))}
    </div>
  );
};

// ─── Top Stat Cards ────────────────────────────────────────────────────────
const ApiStatCard = ({ label, value, suffix = '', change, icon: Icon, accent, isDark }) => {
  const isPositive = change >= 0;
  const formatVal = (v) => {
    if (typeof v !== 'number') return v;
    if (v >= 1000000) return `${(v / 1000000).toFixed(1)}M`;
    if (v >= 1000) return `${(v / 1000).toFixed(0)}K`;
    return v.toLocaleString();
  };
  return (
    <div
      style={{
        background: isDark
          ? `linear-gradient(135deg,#0f1729 0%,rgba(${accent.rgb},0.06) 100%)`
          : `linear-gradient(135deg,#ffffff 0%,rgba(${accent.rgb},0.04) 100%)`,
        border: `1px solid ${isDark ? `rgba(${accent.rgb},0.25)` : `rgba(${accent.rgb},0.15)`}`,
        borderRadius: 16, padding: '20px 24px',
        boxShadow: isDark ? '0 2px 12px rgba(0,0,0,0.22)' : '0 2px 12px rgba(0,0,0,0.06)',
        display: 'flex', flexDirection: 'column', gap: 8, flex: 1, minWidth: 0,
        transition: 'transform 0.2s, box-shadow 0.2s',
      }}
      onMouseEnter={(e) => { e.currentTarget.style.transform = 'translateY(-2px)'; e.currentTarget.style.boxShadow = isDark ? '0 6px 24px rgba(0,0,0,0.32)' : '0 6px 24px rgba(0,0,0,0.12)'; }}
      onMouseLeave={(e) => { e.currentTarget.style.transform = 'translateY(0)'; e.currentTarget.style.boxShadow = isDark ? '0 2px 12px rgba(0,0,0,0.22)' : '0 2px 12px rgba(0,0,0,0.06)'; }}
    >
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <div style={{ width: 40, height: 40, borderRadius: 12, background: `rgba(${accent.rgb},0.13)`, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <Icon size={18} color={accent.hex} />
        </div>
        <span style={{ fontSize: 11, fontWeight: 700, letterSpacing: '0.04em', color: isPositive ? '#10b981' : '#ef4444', background: isPositive ? 'rgba(16,185,129,0.1)' : 'rgba(239,68,68,0.1)', borderRadius: 20, padding: '2px 8px' }}>
          {isPositive ? '+' : ''}{change}%
        </span>
      </div>
      <div>
        <p style={{ fontSize: 11, color: isDark ? '#64748b' : '#94a3b8', margin: 0, fontWeight: 500 }}>{label}</p>
        <p style={{ fontSize: 28, fontWeight: 800, margin: '2px 0 0', color: isDark ? '#f0f6fc' : '#0f172a', fontFamily: "'Outfit',sans-serif", letterSpacing: '-0.5px' }}>
          {formatVal(value)}{suffix}
        </p>
      </div>
    </div>
  );
};

// ─── Heatmap Cell ──────────────────────────────────────────────────────────
const HeatCell = ({ hits, max, isDark, agent, user }) => {
  const [hovered, setHovered] = useState(false);
  const intensity = hits / max;
  const alpha = 0.08 + intensity * 0.88;
  const bg = `rgba(248,114,23,${alpha.toFixed(2)})`;
  const textColor = intensity > 0.4 ? '#374151' : isDark ? '#c9d1d9' : '#374151';
  return (
    <div
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      title={`${user} × ${agent}: ${hits.toLocaleString()} hits`}
      style={{
        background: bg, borderRadius: 6, width: '100%', height: 36,
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        cursor: 'default', fontSize: 10, fontWeight: 600,
        color: textColor,
        transition: 'transform 0.15s, box-shadow 0.15s',
        transform: hovered ? 'scale(1.06)' : 'scale(1)',
        boxShadow: hovered ? '0 4px 16px rgba(248,114,23,0.4)' : 'none',
        border: hovered ? '1px solid rgba(248,114,23,0.6)' : '1px solid transparent',
        position: 'relative', zIndex: hovered ? 2 : 1,
      }}
    >
      {hits > 0 ? hits.toLocaleString() : ''}
    </div>
  );
};

// ─── Toggle Button ─────────────────────────────────────────────────────────
const ToggleBtn = ({ label, active, onClick, isDark }) => (
  <button
    onClick={onClick}
    style={{
      padding: '4px 14px', borderRadius: 20, border: 'none', cursor: 'pointer',
      fontSize: 12, fontWeight: 600, transition: 'all 0.2s',
      background: active ? '#F87217' : isDark ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.05)',
      color: active ? '#fff' : isDark ? '#94a3b8' : '#64748b',
      boxShadow: active ? '0 2px 8px rgba(248,114,23,0.35)' : 'none',
    }}
  >
    {label}
  </button>
);

// ─── Main Component ────────────────────────────────────────────────────────
const ApiHitsSection = ({ isDark, apiData }) => {
  const [timeToggle, setTimeToggle] = useState('daily');

  const lineChartRef  = useRef(null);  // inner scroll div (horizontal scroll)
  const lineCardRef   = useRef(null);  // wraps full ChartCard for html2canvas
  const agentChartRef = useRef(null);  // inner scroll div (vertical scroll)
  const agentCardRef  = useRef(null);  // wraps full ChartCard for html2canvas
  const paretoRef     = useRef(null);  // inner scroll div
  const paretoCardRef = useRef(null);  // wraps full ChartCard for html2canvas
  const heatmapRef    = useRef(null);  // wraps the full ChartCard for html2canvas
  const [lineDl,    setLineDl]    = useState(false);
  const [agentDl,   setAgentDl]   = useState(false);
  const [paretoDl,  setParetoDl]  = useState(false);
  const [heatmapDl, setHeatmapDl] = useState(false);

  const axisColor = isDark ? '#3d4f7a' : '#cbd5e1';
  const gridColor = isDark ? 'rgba(100,160,255,0.06)' : '#f1f5f9';
  const textMuted = isDark ? '#8b949e' : '#64748b';

  // Use API data only (no mock fallback)
  const liveStats = apiData?.stats || EMPTY_API_STATS;
  const liveDaily = Array.isArray(apiData?.daily) ? apiData.daily : [];
  const liveWeekly = Array.isArray(apiData?.weekly) ? apiData.weekly : [];
  const liveByAgent = Array.isArray(apiData?.byAgent) ? apiData.byAgent : [];
  const liveByUser = Array.isArray(apiData?.byUser) ? apiData.byUser : [];
  const liveHeatmapAgents = Array.isArray(apiData?.heatmapAgents) ? apiData.heatmapAgents : [];
  const liveHeatmapData = Array.isArray(apiData?.heatmapData) ? apiData.heatmapData : [];
  
  const maxHitsValue = Math.max(...liveByUser.map(d => d.hits));
  const niceMax = Math.ceil(maxHitsValue / 10) * 10; // round up to nearest 10
  const tickInterval = niceMax <= 50 ? 10 : niceMax <= 200 ? 50 : niceMax <= 1000 ? 100 : 500;
  const hitsTicks = Array.from({ length: Math.floor(niceMax / tickInterval) + 1 }, (_, i) => i * tickInterval);



  const timeData = timeToggle === 'daily' ? liveDaily : liveWeekly;

  const maxHits = useMemo(() => {
    const allHits = liveHeatmapData.flatMap((r) => (r.values || []).map((v) => Number(v.hits) || 0));
    return Math.max(...allHits, 1);
  }, [liveHeatmapData]);

  // ── Download helpers ──
  // Line chart PNG — uses html2canvas; lineChartRef inner div has overflowX:auto
  // which clips the SVG rect, so downloadSVGasPNG captures only the visible width.
  const dlLineChart = useCallback(() =>
    downloadChartPNG(lineCardRef, 'api-hits-over-time', isDark, setLineDl),
  [isDark]);

  const dlLineCSV = useCallback(() =>
    downloadCSV(
      timeData.map((d) => ({ Label: d.label, 'API Hits': d.hits })),
      ['Label', 'API Hits'],
      'api-hits-over-time'
    ), [timeData]);

  // Agent PNG — uses html2canvas; agentChartRef inner div has maxHeight+overflowY:auto
  // which clips the SVG rect, so downloadSVGasPNG would capture only the visible slice.
  const dlAgentPNG = useCallback(() =>
    downloadChartPNG(agentCardRef, 'api-hits-by-agent', isDark, setAgentDl),
  [isDark]);

  const dlAgentCSV = useCallback(() =>
    downloadCSV(
      liveByAgent.map((d) => ({ Agent: d.agent, 'API Hits': d.hits })),
      ['Agent', 'API Hits'],
      'api-hits-by-agent'
    ), [liveByAgent]);

  // Pareto PNG — uses html2canvas because the chart is inside a horizontal scroll container;
  // SVG getBoundingClientRect() returns the clipped visible size (nearly zero), not the full chart.
  const dlParetoPNG = useCallback(() =>
    downloadChartPNG(paretoCardRef, 'api-hits-by-user-pareto', isDark, setParetoDl),
  [isDark]);

  const dlParetoCSV = useCallback(() =>
    downloadCSV(
      liveByUser.map((d) => ({ User: d.user, 'API Hits': d.hits, 'Cumulative %': d.cumulative })),
      ['User', 'API Hits', 'Cumulative %'],
      'api-hits-by-user-pareto'
    ), [liveByUser]);

  // Heatmap PNG — uses html2canvas because the heatmap is an HTML <table>, not SVG
  const dlHeatmapPNG = useCallback(() =>
    downloadChartPNG(heatmapRef, 'user-agent-heatmap', isDark, setHeatmapDl),
  [isDark]);

  const dlHeatmapCSV = useCallback(() => {
    const rows = liveHeatmapData.flatMap((row) =>
      row.values.map((v) => ({ User: row.user, Agent: v.agent, 'API Hits': v.hits }))
    );
    downloadCSV(rows, ['User', 'Agent', 'API Hits'], 'user-agent-heatmap');
  }, [liveHeatmapData]);

  // 70px per bar + 140px reserved for both Y-axes and margins
  const paretoWidth = Math.max(liveByUser.length * 70 + 140, 500);

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 24 }}>

      {/* ── Row 1: Stat Cards ─────────────────────────────────────────────── */}
      <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap' }}>
        <ApiStatCard
          label={liveStats.totalApiHits?.label || 'Total API Hits'}
          value={liveStats.totalApiHits?.value ?? 0}
          change={liveStats.totalApiHits?.change ?? 0}
          icon={BarChart2}
          accent={{ rgb: '248,114,23', hex: '#F87217' }}
          isDark={isDark}
        />
        <ApiStatCard
          label={liveStats.avgHitsPerUser?.label || 'Avg Hits / User'}
          value={liveStats.avgHitsPerUser?.value ?? 0}
          change={liveStats.avgHitsPerUser?.change ?? 0}
          icon={Users}
          accent={{ rgb: '139,92,246', hex: '#8b5cf6' }}
          isDark={isDark}
        />
        <ApiStatCard
          label={liveStats.top10Contribution?.label || 'Top 10% Contribution'}
          value={liveStats.top10Contribution?.value ?? 0}
          suffix="%"
          change={liveStats.top10Contribution?.change ?? 0}
          icon={Activity}
          accent={{ rgb: '6,182,212', hex: '#06b6d4' }}
          isDark={isDark}
        />
      </div>

      {/* ── Row 2: Line Chart ─────────────────────────────────────────────── */}
      <div ref={lineCardRef}>
        <ChartCard
          title="API Hits Over Time"
          subtitle={timeToggle === 'daily' ? 'Last 30 days · daily totals' : 'Last 12 weeks · weekly totals'}
          isDark={isDark}
          action={
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <div style={{ display: 'flex', gap: 4, padding: '2px', background: isDark ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.04)', borderRadius: 24 }}>
                <ToggleBtn label="Daily" active={timeToggle === 'daily'} onClick={() => setTimeToggle('daily')} isDark={isDark} />
                <ToggleBtn label="Weekly" active={timeToggle === 'weekly'} onClick={() => setTimeToggle('weekly')} isDark={isDark} />
              </div>
              <DownloadMenu onPNG={dlLineChart} isDark={isDark} />
            </div>
          }
        >
          {timeData.length === 0 ? (
            <div className="h-[230px] flex items-center justify-center text-xs" style={{ color: textMuted }}>
              No API hit trend data available.
            </div>
          ) : (
            <div
              ref={lineChartRef}
              style={{
                overflowX: 'auto',
                overflowY: 'hidden',
                scrollbarWidth: 'thin',
                scrollbarColor: isDark ? '#3d4f7a #0f1729' : '#cbd5e1 #f8fafc',
              }}
            >
              <div style={{ minWidth: Math.max(timeData.length * 36, 500) }}>
                <ResponsiveContainer width="100%" height={250}>
                  <LineChart data={timeData} margin={{ top: 6, right: 16, bottom: 40, left: 10 }}>
                <defs>
                  <linearGradient id="lineGlow" x1="0" y1="0" x2="1" y2="0">
                    <stop offset="0%" stopColor="#F87217" />
                    <stop offset="100%" stopColor="#f59e0b" />
                  </linearGradient>
                </defs>
                <CartesianGrid stroke={gridColor} vertical={false} />
                <XAxis
                  dataKey="label"
                  tick={{ fontSize: 10, fill: axisColor }}
                  tickLine={false}
                  axisLine={false}
                  interval={0}
                  angle={-35}
                  textAnchor="end"
                  height={55}
                />
                <YAxis
                  tick={{ fontSize: 10, fill: axisColor }}
                  tickLine={false}
                  axisLine={false}
                  width={48}
                  tickFormatter={(v) => v >= 1000 ? `${(v / 1000).toFixed(0)}k` : v}
                />
                <Tooltip content={<CustomTooltip isDark={isDark} />} />
                <Line
                  type="monotone" dataKey="hits" name="API Hits"
                  stroke="url(#lineGlow)" strokeWidth={2.5} dot={false}
                  activeDot={{ r: 5, fill: '#F87217', strokeWidth: 0 }}
                />
                  </LineChart>
                </ResponsiveContainer>
              </div>
            </div>
          )}
          <div style={{ display: 'flex', justifyContent: 'space-between', padding: '8px 12px 0', fontSize: 10, color: textMuted, fontWeight: 500 }}>
            <span>Y-Axis: API Hits</span>
            <span>X-Axis: Date</span>
          </div>
        </ChartCard>
      </div>{/* end lineCardRef */}

      {/* ── Row 3: Bar Chart + Pareto Chart ───────────────────────────────── */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16, minWidth: 0 }}>

        {/* API Hits by Agent – Horizontal Bar with vertical scroll */}
        <div ref={agentCardRef} style={{ minWidth: 0 }}>
        <ChartCard
          title="API Hits by Agent"
          subtitle="Top agents · sorted descending"
          isDark={isDark}
          action={<DownloadMenu onPNG={dlAgentPNG} isDark={isDark} />}
        >
          {liveByAgent.length === 0 ? (
            <div className="h-[280px] flex items-center justify-center text-xs" style={{ color: textMuted }}>
              No per-agent API usage available.
            </div>
          ) : (
            <div
              ref={agentChartRef}
              style={{
                maxHeight: 300,
                overflowY: 'auto',
                overflowX: 'hidden',
                scrollbarWidth: 'thin',
                scrollbarColor: isDark ? '#3d4f7a #0f1729' : '#cbd5e1 #f8fafc',
              }}
            >
              <div style={{ minHeight: liveByAgent.length * 38 + 20 }}>
                <ResponsiveContainer width="100%" height={liveByAgent.length * 38 + 20}>
                  <BarChart layout="vertical" data={liveByAgent} margin={{ top: 4, right: 40, bottom: 4, left: 0 }}>
                    <CartesianGrid stroke={gridColor} horizontal={false} />
                    <XAxis
                      type="number"
                      tick={{ fontSize: 10, fill: axisColor }}
                      tickLine={false}
                      axisLine={false}
                      tickFormatter={(v) => v >= 1000 ? `${(v/1000).toFixed(1)}k` : v}
                    />
                    <YAxis
                      type="category"
                      dataKey="agent"
                      width={140}
                      tick={{ fontSize: 10, fill: textMuted }}
                      tickLine={false}
                      axisLine={false}
                    />
                    <Tooltip content={<CustomTooltip isDark={isDark} />} />
                    <Bar dataKey="hits" name="API Hits" radius={[0, 6, 6, 0]} barSize={16} fill="#F87217" />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </div>
          )}
          <div style={{ display: 'flex', justifyContent: 'space-between', padding: '8px 12px 0', fontSize: 10, color: textMuted, fontWeight: 500 }}>
              <span>Y-Axis: Agents</span>
              <span>X-Axis: API Hits</span>
            </div>
        </ChartCard>
        </div>{/* end agentCardRef */}

        {/* API Hits by User – Pareto Chart with horizontal scroll */}
        <div ref={paretoCardRef} style={{ minWidth: 0 }}>
          <ChartCard
            title="API Hits by User"
            subtitle="Pareto — bars = hits · line = cumulative %"
            isDark={isDark}
            action={<DownloadMenu onPNG={dlParetoPNG} isDark={isDark} />}
          >
            {liveByUser.length === 0 ? (
              <div className="h-[280px] flex items-center justify-center text-xs" style={{ color: textMuted }}>
                No per-user API usage available.
              </div>
            ) : (
              <div
                ref={paretoRef}
                style={{
                  overflowX: 'auto',
                  overflowY: 'hidden',
                  scrollbarWidth: 'thin',
                  scrollbarColor: isDark ? '#3d4f7a #0f1729' : '#cbd5e1 #f8fafc',
                }}
              >
                <div style={{ minWidth: paretoWidth }}>
                  <ResponsiveContainer width="100%" height={310}>
                    <ComposedChart
                      data={liveByUser}
                      margin={{ top: 4, right: 48, bottom: 48, left: 10 }}
                    >
                      <CartesianGrid stroke={gridColor} vertical={false} />
                      <XAxis
                        dataKey="user"
                        tick={{ fontSize: 9, fill: axisColor }}
                        tickLine={false}
                        axisLine={false}
                        interval={0}
                        angle={-35}
                        textAnchor="end"
                        height={60}
                        tickFormatter={(v) => v.length > 20 ? v.slice(0, 20) + '…' : v}
                      />                      

                      <YAxis
                        yAxisId="hits"
                          domain={[0, niceMax]}
                          ticks={hitsTicks}
                          tick={{ fontSize: 10, fill: axisColor }}
                          tickLine={false}
                          axisLine={false}
                          width={44}
                        tickFormatter={(v) => v >= 1000 ? `${(v / 1000).toFixed(1)}k` : v}
                      />
                      <YAxis
                        yAxisId="pct"
                        orientation="right"
                        domain={[0, 100]}
                        tick={{ fontSize: 10, fill: axisColor }}
                        tickLine={false}
                        axisLine={false}
                        width={36}
                        tickFormatter={(v) => `${v}%`}
                      />
                      <Tooltip content={<CustomTooltip isDark={isDark} />} />
                      <Legend
                        verticalAlign="bottom"
                        align="center"
                        iconType="circle"
                        iconSize={8}
                        wrapperStyle={{ paddingTop: 8, fontSize: 10 }}
                        formatter={(val) => (
                          <span style={{ fontSize: 10, color: isDark ? '#94a3b8' : '#64748b' }}>{val}</span>                     )}
/>
                      <Bar yAxisId="hits" dataKey="hits" name="API Hits" fill="#F87217" radius={[4, 4, 0, 0]} barSize={26} />
                      <Line
                        yAxisId="pct"
                        type="monotone"
                        dataKey="cumulative"
                        name="Cumulative %"
                        stroke="#8b5cf6"
                        strokeWidth={2}
                        dot={{ r: 3, fill: '#8b5cf6', strokeWidth: 0 }}
                      />
                    </ComposedChart>
                  </ResponsiveContainer>
                </div>
              </div>
            )}
            <div style={{ display: 'flex', justifyContent: 'space-between', padding: '8px 12px 0', fontSize: 10, color: textMuted, fontWeight: 500 }}>
              <span>Y-Axis: API Hits / Cumulative %</span>
              <span>X-Axis: Users</span>
            </div>
          </ChartCard>
        </div>{/* end paretoCardRef */}

      </div>{/* end Row 3 grid */}

      {/* ── Row 4: Heatmap (Full Width) ───────────────────────────────────── */}
      <div ref={heatmapRef}>
        <ChartCard
          title="User × Agent Interaction Heatmap"
          subtitle="Color intensity = API hits volume · hover for exact value"
          isDark={isDark}
          action={<DownloadMenu onPNG={dlHeatmapPNG} isDark={isDark} />}
        >
          {liveHeatmapData.length === 0 || liveHeatmapAgents.length === 0 ? (
            <div className="h-[240px] flex items-center justify-center text-xs" style={{ color: textMuted }}>
              No heatmap data available.
            </div>
          ) : (
            <div
              style={{
                maxHeight: 380,
                overflow: 'auto',
                scrollbarWidth: 'thin',
                scrollbarColor: isDark ? '#3d4f7a #0f1729' : '#cbd5e1 #f8fafc',
                paddingRight: 4,
              }}
            >
              <table
                style={{
                  width: '100%',
                  minWidth: liveHeatmapAgents.length * 100 + 120,
                  borderCollapse: 'separate',
                  borderSpacing: 4,
                  tableLayout: 'fixed',
                }}
              >
                <thead>
                  <tr>
                    <th
                      style={{
                        position: 'sticky',
                        top: 0,
                        left: 0,
                        zIndex: 20,
                        background: isDark ? '#0f1729' : '#ffffff',
                        width: 120,
                      }}
                    />
                    {liveHeatmapAgents.map((agent) => (
                      <th
                        key={agent}
                        style={{
                          position: 'sticky',
                          top: 0,
                          zIndex: 10,
                          background: isDark ? '#0f1729' : '#ffffff',
                          padding: '8px 4px',
                          fontSize: 10,
                          fontWeight: 600,
                          color: isDark ? '#94a3b8' : '#64748b',
                          textAlign: 'center',
                          whiteSpace: 'nowrap',
                          letterSpacing: '0.02em',
                          borderBottom: `1px solid ${isDark ? '#1e2d52' : '#e2e8f0'}`,
                        }}
                      >
                        {agent}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {liveHeatmapData.map((row) => (
                    <tr key={row.user}>
                      <td
                        style={{
                          position: 'sticky',
                          left: 0,
                          zIndex: 10,
                          background: isDark ? '#0f1729' : '#ffffff',
                          fontSize: 10,
                          fontWeight: 600,
                          color: isDark ? '#94a3b8' : '#64748b',
                          paddingRight: 12,
                          width: 120,         
                          maxWidth: 120,       
                          overflow: 'hidden',  
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap',
                          textAlign: 'right',
                          borderRight: `1px solid ${isDark ? '#1e2d52' : '#e2e8f0'}`,
                          verticalAlign: 'middle',
                        }}
                      >
                        {row.user}
                      </td>
                      {(row.values || []).map(({ agent, hits }) => (
                        <td key={agent} style={{ padding: 2, verticalAlign: 'middle' }}>
                          <HeatCell hits={hits} max={maxHits} isDark={isDark} agent={agent} user={row.user} />
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>

              {/* Gradient legend */}
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 8, marginTop: 12 }}>
                <span style={{ fontSize: 10, color: textMuted }}>Low</span>
                <div style={{ display: 'flex', gap: 2 }}>
                  {[0.08, 0.25, 0.42, 0.60, 0.78, 0.96].map((a, i) => (
                    <div key={i} style={{ width: 22, height: 10, borderRadius: 3, background: `rgba(248,114,23,${a})` }} />
                  ))}
                </div>
                <span style={{ fontSize: 10, color: textMuted }}>High</span>
              </div>
            </div>
          )}
        </ChartCard>
      </div>
    </div>
  );
};

export default ApiHitsSection;

